import { LpLayout } from "@/layouts/lp";
import { microCMSClient } from "@/utils/client";
import React from "react";
import { Head } from "@/components/base/Head";
import { TableOfContents } from "@/components/base/ui/TableOfContents";
import { Article } from "@/components/base/ui/Article";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { Breadcrumb } from "@/components/base/Breadcrumb";
import { ScrollToTop } from "@/components/base/ui/ScrollToTop";

type NewsPost = {
  id: string;
  title: string;
  publishedAt: string;
  description: string;
  createdAt: string;
  revisedAt: string;
  updatedAt: string;
};

const NewsPage = ({ news }: { news: NewsPost }) => {
  const breadcrumbs = [
    {
      title: "お知らせ",
      slug: "/news",
    },
    {
      title: news.title,
    },
  ];

  const generateJsonLd = (news: NewsPost) => {
    const siteUrl = "https://app.s-d-lab.jp";
    const path = `/news/${news.id}`;
    const url = `${siteUrl}${path}`;
    const defaultOgp = `${siteUrl}/ogp.jpg`;

    const imageUrl = defaultOgp;
    const jsonLd = {
      "@context": "http://schema.org",
      "@type": "Article",
      name: news.title,
      headline: news.title,
      datePublished: news.publishedAt,
      dateModified: news.publishedAt,
      url: url,
      mainEntityOfPage: url,
      image: [imageUrl],
      description: news.description,
      author: {
        "@type": "Person",
        name: "戦略ラボ",
      },
      publisher: {
        "@type": "Organization",
        name: "戦略ラボ",
        logo: {
          "@type": "ImageObject",
          url: defaultOgp,
        },
      },
    };
    return JSON.stringify(jsonLd);
  };

  return (
    <>
      <Head
        title={news.title}
        description={news.description.replace(
          /<("[^"]*"|'[^']*'|[^'">])*>/g,
          ""
        )}
        jsonLd={generateJsonLd(news)}
        canonicalPath={`/news/${news.id}`}
      />
      <main className={"md:bg-gray-100 pb-20"}>
        <div className={"mb-10 max-w-[1440px] m-auto px-4 py-2"}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>
        <div className={"max-w-[960px] bg-white m-auto p-5 md:p-10 rounded"}>
          <div className={"mb-14"}>
            <h1 className={"text-xl md:text-3xl font-bold mx:px-0 mb-2"}>
              <span className={"leading-[1.75]"}>{news.title}</span>
            </h1>
            <p className={"text-gray-500"}>
              {format(new Date(news.publishedAt), "yyyy年M月d日", {
                locale: ja,
              })}
            </p>
          </div>
          <article>
            <div className={"max-w-[600px] mx-auto mb-14"}>
              <TableOfContents description={news.description} />
            </div>
            <div className={""}>
              <Article description={news.description} />
            </div>
          </article>
        </div>
        <ScrollToTop />
      </main>
    </>
  );
};

NewsPage.Layout = LpLayout;
export async function getStaticPaths() {
  const response: { contents: any } = await microCMSClient.get({
    endpoint: "news",
  });
  const paths = response.contents.map((content: { id: string }) => ({
    params: { id: content.id },
  }));
  return { paths, fallback: false };
}
export async function getStaticProps(context: { params: { id: string } }) {
  const id = context.params.id;
  const response = await microCMSClient.get({
    endpoint: "news",
    contentId: id,
  });
  return {
    props: {
      news: response,
    },
  };
}
export default NewsPage;
